"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { fetchCostCodes, fetchCostCodeCounts } from "../api/costCodes"

/**
 * Custom hook for fetching and managing cost codes data
 * @param {Object} options - Hook options
 * @param {string} options.token - Auth token
 * @param {number} options.page - Current page
 * @param {number} options.limit - Items per page
 * @param {string} options.search - Search term
 * @returns {Object} - Cost codes data and operations
 */
/**
 * @param {string} token - Auth token
 * @param {number} page - Current page
 * @param {number} limit - Items per page
 * @param {string} search - Search term
 * @param {string} category - Category filter
 */
const useCostCodes = ({ token, page = 1, limit = 20, search = "", category = null, noPagination = false }) => {
  const [codes, setCodes] = useState([])
  const [counts, setCounts] = useState({ total: 0, active: 0, inactive: 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Cache key for memoization
  const cacheKey = useMemo(
    () => `${noPagination ? 'all' : 'p'+page}-${search}-${category || ''}`,
    [noPagination, page, search, category]
  )

  // Memoize results to avoid flicker
  const [cache, setCache] = useState({})

  /**
   * Fetch data, optionally silently (without setting loading state)
   * @param {Object} options
   * @param {boolean} options.silent - If true, do not toggle loading indicator
   */
  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    try {
      // Return cached data if available
      if (cache[cacheKey]) {
        setCodes(cache[cacheKey])
        return
      }
      // Build params for API calls
      const codeParams = noPagination
        ? { noPagination: true, q: search, category }
        : { page, limit, q: search, category }
      const [codesData, countsData] = await Promise.all([
        fetchCostCodes(token, codeParams),
        fetchCostCodeCounts(token, { category, q: search }),
      ])

      setCodes(codesData)
      setCounts(countsData)
      // Cache results
      setCache(prev => ({ ...prev, [cacheKey]: codesData }))
    } catch (err) {
      setError(err.message || "Failed to fetch cost codes")
      console.error("Error fetching cost codes:", err)
    } finally {
      if (!silent) {
        setLoading(false)
      }
    }
  }, [token, page, limit, search, category, cacheKey, cache, noPagination])

  // Refetch data
  const refetch = useCallback(() => {
    // Clear cache for current key to force refetch
    setCache((prev) => {
      const newCache = { ...prev }
      delete newCache[cacheKey]
      return newCache
    })

    // Silent refetch to avoid loading spinner
    return fetchData({ silent: true })
  }, [fetchData, cacheKey])

  // Initial data fetch
  useEffect(() => {
    if (token) {
      fetchData()
    }
  }, [token, fetchData])

  return {
    codes,
    counts,
    loading,
    error,
    refetch,
  }
}

export default useCostCodes
